'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CGPoint, ConstrainedGeometryBlock } from '@jeraisonne/ai/visual-blocks';
import { measureSegment, measureAngleDeg, formatMeasure } from '@jeraisonne/ai/cg-measures';
import { ZoomIn, ZoomOut, RotateCw, Move, RotateCcw, Minus, Plus, Hand } from 'lucide-react';
import { evalCGValue, evalExpr } from './constrained-geometry/expr';
import { solve } from './constrained-geometry/solver';
import {
  IDENTITY_VIEW,
  applyPinch,
  panBy,
  pinchMetrics,
  rotateBy,
  viewToTransform,
  zoomBy,
  type PinchStart,
  type ViewTransform,
} from './constrained-geometry/view-transform';
import { sampleFunctionPath } from './plot/sample-function';
import { figureCenter, pointNamePlacement, segmentLabelPlacement } from './segment-label';

/** Épaisseur des traits (fins : la figure s'écrit sur la feuille, comme au crayon). */
const STROKE = 1.5;
const THIN = 1.25;
import { angleMark } from './angle-mark';

const VB_W = 360;
const VB_H = 280;
const PAD = 28; // marge en pixels viewBox

/** Nombre à la française, au pas du curseur (« 2,5 », « −1 »). PUR. */
export function formatFigureNumber(value: number, step = 0.1): string {
  if (!Number.isFinite(value)) return '—';
  const decimals = Math.min(
    4,
    Math.max(0, (String(step).split('.')[1] ?? '').replace(/0+$/, '').length),
  );
  const rounded = Number(value.toFixed(decimals));
  return String(Object.is(rounded, -0) ? 0 : rounded)
    .replace('.', ',')
    .replace('-', '−');
}

/** Valeur suivante d'un curseur (± un pas), bornée et sans bruit d'arrondi. PUR. */
export function stepSlider(
  value: number,
  dir: 1 | -1,
  opts: { min: number; max: number; step: number },
): number {
  const next = value + dir * opts.step;
  const snapped = Math.round((next - opts.min) / opts.step) * opts.step + opts.min;
  return Number(Math.min(opts.max, Math.max(opts.min, snapped)).toFixed(6));
}

interface View {
  s: number;
  padX: number;
  padY: number;
  xMin: number;
  yMax: number;
}

export function computeView(points: CGPoint[], block: ConstrainedGeometryBlock): View {
  let xMin: number;
  let xMax: number;
  let yMin: number;
  let yMax: number;
  if (block.viewport) {
    ({ xMin, xMax, yMin, yMax } = block.viewport);
  } else {
    const xs = points.map((p) => p.x);
    const ys = points.map((p) => p.y);
    // Les cercles comptent aussi (sinon un cercle autour d'un point est coupé).
    // Rayon évalué avec les valeurs DÉCLARÉES des curseurs : le cadre ne bouge
    // pas quand l'élève déplace un curseur.
    const declared: Record<string, number> = {};
    for (const v of block.variables ?? []) declared[v.name.toLowerCase()] = v.value;
    if (block.parameter) declared[block.parameter.name.toLowerCase()] = block.parameter.value;
    for (const d of block.drawables ?? []) {
      if (d.kind !== 'circle') continue;
      const c = points.find((p) => p.id === d.center);
      const r = evalCGValue(d.radius, declared, NaN);
      if (!c || !Number.isFinite(r) || r <= 0) continue;
      xs.push(c.x - r, c.x + r);
      ys.push(c.y - r, c.y + r);
    }
    xMin = Math.min(...xs, 0) - 1;
    xMax = Math.max(...xs, 0) + 1;
    yMin = Math.min(...ys, 0) - 1;
    yMax = Math.max(...ys, 0) + 1;
  }
  const rangeX = Math.max(xMax - xMin, 1e-6);
  const rangeY = Math.max(yMax - yMin, 1e-6);
  const s = Math.min((VB_W - 2 * PAD) / rangeX, (VB_H - 2 * PAD) / rangeY);
  const padX = (VB_W - rangeX * s) / 2;
  const padY = (VB_H - rangeY * s) / 2;
  return { s, padX, padY, xMin, yMax };
}

export interface ConstrainedGeometryBlockRendererProps {
  block: ConstrainedGeometryBlock;
  /**
   * Si fourni (mode éditeur), la position finale des points après un drag est
   * persistée. Sinon (mode élève/player) le drag reste éphémère.
   */
  onPointsChange?: (points: CGPoint[]) => void;
  /**
   * Re-résoudre automatiquement à chaque changement du bloc (défaut `true` :
   * élève/player). En mode éditeur on passe `false` → le solveur ne se déclenche
   * PAS spontanément à chaque édition de contrainte (évite les états instables) ;
   * l'enseignant applique les contraintes explicitement (bouton « Recalculer »).
   * Le drag reste interactif dans les deux cas.
   */
  autoSolve?: boolean;
}

export function ConstrainedGeometryBlockRenderer({
  block,
  onPointsChange,
  autoSolve = true,
}: ConstrainedGeometryBlockRendererProps) {
  const interactive = block.interactive !== false;
  // `onPointsChange` n'est fourni que par l'éditeur (aperçu prof). Côté élève
  // (player) il est absent → c'est là, et SEULEMENT là, qu'on applique la policy.
  const editing = !!onPointsChange;
  const policy = block.interactionPolicy;
  const isDraggable = useCallback(
    (p: CGPoint): boolean => {
      if (!interactive || p.fixed) return false;
      if (editing) return true; // le prof manipule toute la figure dans l'aperçu
      if (policy?.draggablePoints) return policy.draggablePoints.includes(p.id);
      return true;
    },
    [interactive, editing, policy],
  );
  // Tous les curseurs (paramètre legacy `x` + variables) sont exposés ensemble.
  const slidersShown = editing || policy?.showSlider !== false;
  const variables = block.variables ?? [];

  /** Portée d'évaluation par défaut, depuis les valeurs déclarées du bloc. */
  const initialScope = (): Record<string, number> => {
    const s: Record<string, number> = {};
    for (const v of block.variables ?? []) s[v.name.toLowerCase()] = v.value;
    if (block.parameter) s[block.parameter.name.toLowerCase()] = block.parameter.value;
    return s;
  };

  const [xValue, setXValue] = useState(block.parameter?.value ?? 0);
  const [varValues, setVarValues] = useState<Record<string, number>>(() =>
    Object.fromEntries(variables.map((v) => [v.name.toLowerCase(), v.value])),
  );
  const scope = useMemo(() => {
    const s: Record<string, number> = { ...varValues };
    if (block.parameter) s[block.parameter.name.toLowerCase()] = xValue;
    return s;
  }, [varValues, xValue, block.parameter]);

  const [points, setPoints] = useState<CGPoint[]>(() =>
    autoSolve ? solve(block.points, block.constraints, initialScope()) : block.points,
  );
  const svgRef = useRef<SVGSVGElement>(null);
  const contentRef = useRef<SVGGElement>(null);
  // L'élève a déjà manipulé la figure : l'invitation « fais glisser » disparaît.
  const [touched, setTouched] = useState(false);
  const dragIdRef = useRef<string | null>(null);

  // ── Transformation de VUE (pan / zoom / rotation) — purement visuelle ────────
  const CENTER_X = VB_W / 2;
  const CENTER_Y = VB_H / 2;
  const [vt, setVt] = useState<ViewTransform>(IDENTITY_VIEW);
  // Mode du drag souris (desktop) : déplacer la vue ou la faire pivoter.
  const [dragMode, setDragMode] = useState<'translate' | 'rotate'>('translate');
  const pointersRef = useRef<Map<number, { x: number; y: number }>>(new Map());
  const panStartRef = useRef<{ x: number; y: number; view: ViewTransform } | null>(null);
  const pinchStartRef = useRef<PinchStart | null>(null);

  // Signature des données d'entrée : re-résoudre quand le bloc change (édition).
  const inputSig = useMemo(
    () =>
      JSON.stringify([
        block.points,
        block.constraints,
        block.parameter,
        block.variables,
        block.viewport,
      ]),
    [block.points, block.constraints, block.parameter, block.variables, block.viewport],
  );
  useEffect(() => {
    setXValue(block.parameter?.value ?? 0);
    setVarValues(
      Object.fromEntries((block.variables ?? []).map((v) => [v.name.toLowerCase(), v.value])),
    );
    // autoSolve=false (éditeur) : on synchronise les points bruts SANS résoudre,
    // pour ne pas relancer le solveur à chaque édition de contrainte.
    setPoints(autoSolve ? solve(block.points, block.constraints, initialScope()) : block.points);
  }, [inputSig, autoSolve, block.points, block.constraints, block.parameter, block.variables]);

  const byId = useMemo(() => {
    const m = new Map<string, CGPoint>();
    for (const p of points) m.set(p.id, p);
    return m;
  }, [points]);

  const view = useMemo(() => computeView(points, block), [points, block]);
  const tx = (mx: number) => (mx - view.xMin) * view.s + view.padX;
  const ty = (my: number) => (view.yMax - my) * view.s + view.padY;

  const clientToMath = useCallback(
    (clientX: number, clientY: number): { x: number; y: number } | null => {
      const svg = svgRef.current;
      const g = contentRef.current;
      if (!svg || !g) return null;
      // CTM du GROUPE transformé → prend en compte le pan/zoom/rotation de la vue,
      // pour que le drag des points reste correct quelle que soit la vue.
      const ctm = g.getScreenCTM();
      if (!ctm) return null;
      const pt = svg.createSVGPoint();
      pt.x = clientX;
      pt.y = clientY;
      const loc = pt.matrixTransform(ctm.inverse());
      return {
        x: (loc.x - view.padX) / view.s + view.xMin,
        y: view.yMax - (loc.y - view.padY) / view.s,
      };
    },
    [view],
  );

  /** Client px → px viewBox (espace AVANT la transform de vue) — pour les gestes. */
  const clientToSvg = (clientX: number, clientY: number) => {
    const r = svgRef.current?.getBoundingClientRect();
    if (!r || r.width === 0 || r.height === 0) return { x: 0, y: 0 };
    return { x: ((clientX - r.left) / r.width) * VB_W, y: ((clientY - r.top) / r.height) * VB_H };
  };

  const onPointerDown = (id: string) => (e: React.PointerEvent) => {
    const p = byId.get(id);
    if (!p || !isDraggable(p)) return;
    e.preventDefault();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    dragIdRef.current = id;
    setTouched(true);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const id = dragIdRef.current;
    if (!id) return;
    const m = clientToMath(e.clientX, e.clientY);
    if (!m) return;
    setPoints((prev) => {
      // Le point draggé est AMORCÉ au curseur mais PAS épinglé : le solveur peut
      // le déplacer pour satisfaire les contraintes (ses coordonnées ne « font pas
      // foi »). Évite les blocages quand le curseur viole les contraintes.
      const moved = prev.map((p) => (p.id === id ? { ...p, x: m.x, y: m.y } : p));
      return solve(moved, block.constraints, scope);
    });
  };

  const endDrag = () => {
    if (!dragIdRef.current) return;
    dragIdRef.current = null;
    if (onPointsChange) onPointsChange(points);
  };

  const paramKey = block.parameter?.name.toLowerCase();
  const onXChange = (next: number) => {
    setXValue(next);
    const s = { ...varValues, ...(paramKey ? { [paramKey]: next } : {}) };
    setPoints((prev) => solve(prev, block.constraints, s));
  };
  const onVarChange = (name: string, next: number) => {
    const nv = { ...varValues, [name.toLowerCase()]: next };
    setVarValues(nv);
    const s = { ...nv, ...(paramKey ? { [paramKey]: xValue } : {}) };
    setPoints((prev) => solve(prev, block.constraints, s));
  };

  // ── Gestes de vue (pan 1 doigt / pinch+rotation 2 doigts) sur la surface ─────
  // Coexistent avec le drag de point : si un point est saisi (dragIdRef posé par
  // le cercle), on laisse le drag prioritaire.
  const onSurfacePointerDown = (e: React.PointerEvent) => {
    if (dragIdRef.current) return; // un point est en cours de drag
    const p = clientToSvg(e.clientX, e.clientY);
    pointersRef.current.set(e.pointerId, p);
    svgRef.current?.setPointerCapture?.(e.pointerId);
    if (pointersRef.current.size === 1) {
      panStartRef.current = { x: p.x, y: p.y, view: vt };
      pinchStartRef.current = null;
    } else if (pointersRef.current.size === 2) {
      const [a, b] = [...pointersRef.current.values()];
      const m = pinchMetrics(a!, b!);
      pinchStartRef.current = { dist: m.dist, angle: m.angle, cx: m.cx, cy: m.cy, view: vt };
      panStartRef.current = null;
    }
  };

  const onSurfacePointerMove = (e: React.PointerEvent) => {
    if (dragIdRef.current) {
      onPointerMove(e); // drag d'un point — prioritaire
      return;
    }
    if (!pointersRef.current.has(e.pointerId)) return;
    pointersRef.current.set(e.pointerId, clientToSvg(e.clientX, e.clientY));
    if (pointersRef.current.size >= 2 && pinchStartRef.current) {
      const [a, b] = [...pointersRef.current.values()];
      const m = pinchMetrics(a!, b!);
      setVt(applyPinch(pinchStartRef.current, m.dist, m.angle, m.cx, m.cy));
    } else if (panStartRef.current) {
      const p = pointersRef.current.get(e.pointerId)!;
      const start = panStartRef.current;
      if (dragMode === 'rotate') {
        // Rotation autour du centre selon l'angle balayé par le curseur.
        const a0 = Math.atan2(start.y - CENTER_Y, start.x - CENTER_X);
        const a1 = Math.atan2(p.y - CENTER_Y, p.x - CENTER_X);
        setVt(rotateBy({ ...start.view }, a1 - a0));
      } else {
        setVt(panBy(start.view, p.x - start.x, p.y - start.y));
      }
    }
  };

  const onSurfacePointerUp = (e: React.PointerEvent) => {
    if (dragIdRef.current) {
      endDrag();
      return;
    }
    pointersRef.current.delete(e.pointerId);
    svgRef.current?.releasePointerCapture?.(e.pointerId);
    if (pointersRef.current.size < 2) pinchStartRef.current = null;
    if (pointersRef.current.size === 1) {
      const only = [...pointersRef.current.values()][0]!;
      panStartRef.current = { x: only.x, y: only.y, view: vt };
    } else if (pointersRef.current.size === 0) {
      panStartRef.current = null;
    }
  };

  // Molette : on ne zoome qu'avec Ctrl / ⌘ (ou le pincement du pavé tactile,
  // qui arrive avec ctrlKey) — sinon la page défile normalement. Écouteur non
  // passif : React n'en pose que des passifs, preventDefault y est ignoré.
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      setVt((v) => zoomBy(v, e.deltaY < 0 ? 1.1 : 1 / 1.1));
    };
    svg.addEventListener('wheel', onWheel, { passive: false });
    return () => svg.removeEventListener('wheel', onWheel);
  }, []);

  /** Revenir au départ : vue, curseurs et points d'origine. */
  const resetAll = () => {
    setVt(IDENTITY_VIEW);
    const s = initialScope();
    setXValue(block.parameter?.value ?? 0);
    setVarValues(
      Object.fromEntries((block.variables ?? []).map((v) => [v.name.toLowerCase(), v.value])),
    );
    setPoints(autoSolve ? solve(block.points, block.constraints, s) : block.points);
  };
  const hasDraggable = points.some((p) => isDraggable(p));

  const TOOLBTN =
    'flex h-10 w-10 items-center justify-center rounded-xl border shadow-sm backdrop-blur-sm transition-colors active:scale-95 sm:h-9 sm:w-9';
  const TOOL_IDLE =
    'border-[#d6e2f5] bg-[#ffffff]/90 text-[#1e3a8a] hover:bg-[#eef4ff] hover:text-[#0a2e57]';

  const pt = (id: string) => {
    const p = byId.get(id);
    return p ? { x: tx(p.x), y: ty(p.y) } : null;
  };
  // Cotes des segments décalées vers l'extérieur de la figure (jamais sur le trait).
  const figCenter = figureCenter(points.map((p) => ({ x: tx(p.x), y: ty(p.y) })));

  // Domaine par défaut des courbes `function` (viewport, sinon emprise des points).
  const fnDomain: [number, number] = block.viewport
    ? [block.viewport.xMin, block.viewport.xMax]
    : [Math.min(...points.map((p) => p.x), 0) - 1, Math.max(...points.map((p) => p.x), 0) + 1];
  /** Évaluateur sûr d'une expression dans la portée courante (x = abscisse). */
  const fnEval = (expr: string) => (x: number) => {
    try {
      const v = evalExpr(expr, { ...scope, x });
      return Number.isFinite(v) ? v : NaN;
    } catch {
      return NaN;
    }
  };

  // Mesures en direct (configurées par le prof) — recalculées à chaque drag/slider.
  const measureLabels = useMemo(() => {
    const m = policy?.measures;
    if (!m) return [];
    const nameOf = (id: string) => byId.get(id)?.name ?? id;
    const out: string[] = [];
    for (const [a, b] of m.segments ?? []) {
      const len = measureSegment(points, a, b);
      if (len != null)
        out.push(`${nameOf(a)}${nameOf(b)} = ${formatMeasure(len).replace('.', ',')}`);
    }
    for (const [a, v, b] of m.angles ?? []) {
      const deg = measureAngleDeg(points, a, v, b);
      if (deg != null)
        out.push(
          `∠${nameOf(a)}${nameOf(v)}${nameOf(b)} = ${formatMeasure(deg).replace('.', ',')}°`,
        );
    }
    return out;
  }, [policy, points, byId]);

  // Les libellés doivent rester LISIBLES malgré la rotation de vue : on les
  // contre-tourne autour de leur ancre (la position suit la figure, le texte non).
  const rotDeg = (vt.rot * 180) / Math.PI;
  const uprightLabel = (x: number, y: number): string | undefined =>
    Math.abs(rotDeg) < 1e-6
      ? undefined
      : `rotate(${(-rotDeg).toFixed(3)} ${x.toFixed(2)} ${y.toFixed(2)})`;

  return (
    <div className="w-full space-y-2">
      <div className="relative mx-auto w-full max-w-lg">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${VB_W} ${VB_H}`}
          className="block w-full touch-pan-y select-none"
          role="img"
          aria-label={block.caption ?? block.title ?? 'Figure géométrique dynamique'}
          onPointerDown={onSurfacePointerDown}
          onPointerMove={onSurfacePointerMove}
          onPointerUp={onSurfacePointerUp}
          onPointerCancel={onSurfacePointerUp}
          onPointerLeave={() => {
            if (dragIdRef.current) endDrag();
          }}
        >
          <g ref={contentRef} transform={viewToTransform(vt, CENTER_X, CENTER_Y)}>
            {/* Tracés */}
            {block.drawables.map((d) => {
              if (d.kind === 'segment') {
                const A = pt(d.a);
                const B = pt(d.b);
                if (!A || !B) return null;
                return (
                  <g key={d.id}>
                    <line
                      x1={A.x}
                      y1={A.y}
                      x2={B.x}
                      y2={B.y}
                      stroke={d.color ?? '#374151'}
                      strokeWidth={d.strokeWidth ?? STROKE}
                      strokeLinecap="round"
                      strokeDasharray={d.dashed ? '6 4' : undefined}
                    />
                    {d.label &&
                      (() => {
                        // Vue tournée : le texte est redressé autour de son point,
                        // on le centre donc sur ce point (décalage un peu plus grand).
                        const rotated = Math.abs(rotDeg) >= 1e-6;
                        const lp = segmentLabelPlacement(A, B, figCenter, rotated ? 14 : 10);
                        return (
                          <text
                            x={lp.x}
                            y={lp.y}
                            dy={rotated ? '0.35em' : lp.dy}
                            textAnchor={rotated ? 'middle' : lp.textAnchor}
                            fontSize={11.5}
                            fill={d.color ?? '#374151'}
                            stroke="#fcfdff"
                            strokeWidth={3}
                            strokeLinejoin="round"
                            paintOrder="stroke"
                            transform={uprightLabel(lp.x, lp.y)}
                          >
                            {d.label}
                          </text>
                        );
                      })()}
                  </g>
                );
              }
              if (d.kind === 'line') {
                const A = pt(d.a);
                const B = pt(d.b);
                if (!A || !B) return null;
                const dx = B.x - A.x;
                const dy = B.y - A.y;
                const t = Math.max(VB_W, VB_H) * 2;
                return (
                  <line
                    key={d.id}
                    x1={A.x - dx * t}
                    y1={A.y - dy * t}
                    x2={A.x + dx * t}
                    y2={A.y + dy * t}
                    stroke={d.color ?? '#374151'}
                    strokeWidth={d.strokeWidth ?? THIN}
                    strokeDasharray={d.dashed ? '6 4' : undefined}
                  />
                );
              }
              if (d.kind === 'circle') {
                const c = pt(d.center);
                if (!c) return null;
                const r = evalCGValue(d.radius, scope) * view.s;
                return (
                  <circle
                    key={d.id}
                    cx={c.x}
                    cy={c.y}
                    r={r}
                    fill="none"
                    stroke={d.color ?? '#374151'}
                    strokeWidth={d.strokeWidth ?? STROKE}
                  />
                );
              }
              if (d.kind === 'polygon') {
                const pts = d.points.map((id) => pt(id)).filter(Boolean) as {
                  x: number;
                  y: number;
                }[];
                if (pts.length < 2) return null;
                const path =
                  pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z';
                return (
                  <path
                    key={d.id}
                    d={path}
                    fill={d.fill ?? 'rgba(99,102,241,0.08)'}
                    stroke={d.stroke ?? '#374151'}
                    strokeWidth={d.strokeWidth ?? STROKE}
                    strokeLinejoin="round"
                  />
                );
              }
              if (d.kind === 'angle') {
                const V = pt(d.vertex);
                const A = pt(d.a);
                const B = pt(d.b);
                if (!V || !A || !B) return null;
                const stroke = d.color ?? '#6366f1';
                // Angle saillant, arc dans le bon sens, étiquette sur la bissectrice.
                const mark = angleMark(V, A, B);
                return (
                  <g key={d.id}>
                    <path
                      d={mark.path}
                      fill="none"
                      stroke={stroke}
                      strokeWidth={d.strokeWidth ?? (mark.isRight ? 1.2 : 1.5)}
                    />
                    {d.label && (
                      <text
                        x={mark.label.x}
                        y={mark.label.y}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fontSize={11}
                        fill={stroke}
                        transform={uprightLabel(mark.label.x, mark.label.y)}
                      >
                        {d.label}
                      </text>
                    )}
                  </g>
                );
              }
              if (d.kind === 'function') {
                const domain = d.domain ?? fnDomain;
                const path = sampleFunctionPath(fnEval(d.expr), domain, { tx, ty });
                if (!path) return null;
                return (
                  <path
                    key={d.id}
                    d={path}
                    fill="none"
                    stroke={d.color ?? '#2563eb'}
                    strokeWidth={d.strokeWidth ?? 1.75}
                  />
                );
              }
              return null;
            })}

            {/* Points (draggables si non-fixed) */}
            {points.map((p) => {
              const c = pt(p.id);
              if (!c) return null;
              const draggable = isDraggable(p);
              const color = p.color ?? (p.fixed ? '#9ca3af' : '#2563eb');
              return (
                <g key={p.id} data-point={p.id} data-draggable={draggable || undefined}>
                  {draggable && (
                    <circle
                      cx={c.x}
                      cy={c.y}
                      r={11}
                      fill={color}
                      fillOpacity={0.16}
                      className={touched ? undefined : 'motion-safe:animate-pulse'}
                      pointerEvents="none"
                    />
                  )}
                  <circle
                    cx={c.x}
                    cy={c.y}
                    r={draggable ? 5 : 3.5}
                    fill={color}
                    stroke="#fff"
                    strokeWidth={1.5}
                    pointerEvents="none"
                  />
                  {/* Zone de prise large (doigt) : invisible, sans défilement. */}
                  {draggable && (
                    <circle
                      cx={c.x}
                      cy={c.y}
                      r={18}
                      fill="transparent"
                      className="touch-none"
                      style={{ cursor: 'grab' }}
                      onPointerDown={onPointerDown(p.id)}
                    />
                  )}
                  {p.name &&
                    (() => {
                      const np = pointNamePlacement(c, figCenter);
                      return (
                        <text
                          x={np.x}
                          y={np.y}
                          textAnchor={np.textAnchor}
                          dy={np.dy}
                          fontSize={12}
                          fontWeight={700}
                          fill="#0f172a"
                          stroke="#fcfdff"
                          strokeWidth={3}
                          strokeLinejoin="round"
                          paintOrder="stroke"
                          transform={uprightLabel(np.x, np.y)}
                        >
                          {p.name}
                        </text>
                      );
                    })()}
                </g>
              );
            })}
          </g>
        </svg>
        <div data-visual-toolbar className="absolute right-1.5 top-1.5 flex gap-1">
          <button
            type="button"
            onClick={() => setVt((v) => zoomBy(v, 1.2))}
            title="Zoom avant"
            aria-label="Zoom avant"
            className={`${TOOLBTN} ${TOOL_IDLE}`}
          >
            <ZoomIn className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setVt((v) => zoomBy(v, 1 / 1.2))}
            title="Zoom arrière"
            aria-label="Zoom arrière"
            className={`${TOOLBTN} ${TOOL_IDLE}`}
          >
            <ZoomOut className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setDragMode((m) => (m === 'translate' ? 'rotate' : 'translate'))}
            title={
              dragMode === 'rotate'
                ? 'Glisser = pivoter (cliquer pour déplacer)'
                : 'Glisser = déplacer (cliquer pour pivoter)'
            }
            aria-label="Basculer déplacer / pivoter"
            aria-pressed={dragMode === 'rotate'}
            className={`${TOOLBTN} ${
              dragMode === 'rotate' ? 'border-[#2563eb] bg-[#dbeafe] text-[#1d4ed8]' : TOOL_IDLE
            }`}
          >
            {dragMode === 'rotate' ? (
              <RotateCw className="h-4 w-4" />
            ) : (
              <Move className="h-4 w-4" />
            )}
          </button>
          <button
            type="button"
            onClick={resetAll}
            title="Revenir au départ"
            aria-label="Revenir au départ"
            className={`${TOOLBTN} ${TOOL_IDLE}`}
          >
            <RotateCcw className="h-4 w-4" />
          </button>
        </div>
        {interactive && hasDraggable && !touched && (
          <p
            data-figure-hint
            className="pointer-events-none absolute bottom-1.5 left-1/2 inline-flex -translate-x-1/2 items-center gap-1 whitespace-nowrap rounded-full bg-[#0a2e57]/85 px-2.5 py-1 text-[11px] font-semibold text-[#ffffff] shadow-sm"
          >
            <Hand className="h-3.5 w-3.5" aria-hidden />
            Fais glisser les points bleus
          </p>
        )}
      </div>

      {slidersShown && (block.parameter || variables.length > 0) && (
        <div className="space-y-1.5 px-1">
          {block.parameter && (
            <FigureSlider
              name={block.parameter.name}
              value={xValue}
              min={block.parameter.min}
              max={block.parameter.max}
              step={block.parameter.step ?? 0.1}
              onChange={(v) => {
                setTouched(true);
                onXChange(v);
              }}
            />
          )}
          {variables.map((v) => {
            const key = v.name.toLowerCase();
            return (
              <FigureSlider
                key={key}
                name={v.name}
                value={varValues[key] ?? v.value}
                min={v.min ?? 0}
                max={v.max ?? 10}
                step={v.step ?? 0.1}
                onChange={(next) => {
                  setTouched(true);
                  onVarChange(v.name, next);
                }}
              />
            );
          })}
        </div>
      )}

      {measureLabels.length > 0 && (
        <div className="flex flex-wrap justify-center gap-1.5 px-1" aria-label="Mesures">
          {measureLabels.map((label) => (
            <span
              key={label}
              data-figure-measure
              className="rounded-full border border-[#d6e2f5] bg-[#ffffff] px-2.5 py-1 text-xs font-semibold tabular-nums text-[#1e3a8a]"
            >
              {label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Curseur d'une figure : nom + valeur lisibles, réglette, et deux boutons
 * − / + (un pas) pour régler au doigt sans viser la poignée.
 */
function FigureSlider({
  name,
  value,
  min,
  max,
  step,
  onChange,
}: {
  name: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
}) {
  const BTN =
    'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[#d6e2f5] bg-[#ffffff] text-[#1e3a8a] transition active:scale-95 disabled:opacity-40 sm:h-8 sm:w-8';
  return (
    <div
      data-figure-slider
      className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-xl border border-[#e3eaf6] bg-[#ffffff]/85 px-2 py-1.5"
    >
      <span className="w-full shrink-0 text-sm tabular-nums text-[#0f172a] sm:w-auto sm:min-w-[4.5rem]">
        <span className="font-semibold italic">{name}</span> ={' '}
        <strong className="text-[#1d4ed8]">{formatFigureNumber(value, step)}</strong>
      </span>
      <button
        type="button"
        aria-label={`Diminuer ${name}`}
        disabled={value <= min}
        onClick={() => onChange(stepSlider(value, -1, { min, max, step }))}
        className={BTN}
        data-figure-step
      >
        <Minus className="h-4 w-4" aria-hidden />
      </button>
      <input
        type="range"
        aria-label={`Valeur de ${name}`}
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="h-8 min-w-0 flex-1 accent-[#2563eb]"
      />
      <button
        type="button"
        aria-label={`Augmenter ${name}`}
        disabled={value >= max}
        onClick={() => onChange(stepSlider(value, 1, { min, max, step }))}
        className={BTN}
        data-figure-step
      >
        <Plus className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
}
